import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, AlertCircle } from "lucide-react";
import { api, trackEvent } from "@/lib/api";

/**
 * /menu/:brandId — public PDF menu viewer that embeds the brand's menu PDF
 * inline. Works even when the PDF host serves it as an attachment, because
 * we wrap it in an iframe / Google Docs viewer for mobile fallback.
 */
export default function MenuViewer() {
  const { brandId } = useParams();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeFailed, setIframeFailed] = useState(false);

  useEffect(() => {
    api
      .get("/brands")
      .then((r) => {
        const found = (r.data || []).find((b) => b.id === brandId);
        setBrand(found || null);
        setLoading(false);
        if (found && found.menu_pdf_url) {
          trackEvent("menu_view", {
            brand_id: found.id,
            brand_name: found.name,
          });
        }
      })
      .catch(() => {
        setBrand(null);
        setLoading(false);
      });
  }, [brandId]);

  if (loading) {
    return (
      <main className="bg-[var(--bg)] text-[var(--text)] min-h-screen flex items-center justify-center" data-testid="menu-loading">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted">Loading menu…</p>
      </main>
    );
  }

  if (!brand) {
    return (
      <main className="bg-[var(--bg)] text-[var(--text)] min-h-screen flex flex-col items-center justify-center p-6" data-testid="menu-not-found">
        <AlertCircle size={32} className="text-[var(--error)]" />
        <h1 className="font-display text-[32px] mt-4">Menu not found</h1>
        <p className="text-muted text-[13px] mt-2">This brand or menu may have moved.</p>
        <Link to="/" className="btn-ghost mt-6">
          <ArrowLeft size={14} /> Back to Naami
        </Link>
      </main>
    );
  }

  if (!brand.menu_pdf_url) {
    return (
      <main className="bg-[var(--bg)] text-[var(--text)] min-h-screen flex flex-col items-center justify-center p-6" data-testid="menu-empty">
        <h1 className="font-display text-[32px]">{brand.name}</h1>
        <p className="text-muted text-[13px] mt-2">No menu uploaded yet.</p>
        <Link to="/" className="btn-ghost mt-6">
          <ArrowLeft size={14} /> Back to Naami
        </Link>
      </main>
    );
  }

  // Google Docs viewer fallback for mobile / browsers that block iframe PDFs
  const gdocsViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
    brand.menu_pdf_url
  )}&embedded=true`;

  return (
    <main className="bg-[var(--bg)] text-[var(--text)] min-h-screen flex flex-col" data-testid="menu-viewer">
      <header className="border-b border-[var(--border)] bg-[var(--bg)]/95 backdrop-blur-md">
        <div className="container-nk py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted hover:text-gold font-mono text-[11px] uppercase tracking-[0.18em] transition-colors"
            data-testid="menu-back-home"
          >
            <ArrowLeft size={14} />
            Back to Naami
          </Link>
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center font-display text-[14px] flex-shrink-0"
              style={{ background: brand.color || "#C8A97E", color: "#0c0c0c" }}
              aria-hidden
            >
              {brand.logo_letter || brand.name?.slice(0, 2).toUpperCase()}
            </div>
            <h1 className="font-display text-[18px] md:text-[22px] leading-none truncate" data-testid="menu-brand-name">
              {brand.name} <span className="text-muted text-[13px] font-mono uppercase tracking-[0.16em] ml-2">Menu</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={brand.menu_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
              style={{ padding: "8px 12px", fontSize: "10.5px" }}
              data-testid="menu-open-tab"
            >
              <ExternalLink size={12} /> Open
            </a>
            <a
              href={brand.menu_pdf_url}
              download={`${brand.name}-menu.pdf`}
              className="btn-primary"
              style={{ padding: "8px 14px", fontSize: "10.5px" }}
              data-testid="menu-download"
              onClick={() =>
                trackEvent("menu_download", {
                  brand_id: brand.id,
                  brand_name: brand.name,
                })
              }
            >
              <Download size={12} /> Download
            </a>
          </div>
        </div>
      </header>

      <div className="flex-1 bg-[var(--surface)]" data-testid="menu-pdf-container">
        {!iframeFailed ? (
          <iframe
            src={brand.menu_pdf_url}
            title={`${brand.name} menu`}
            className="w-full h-full border-0"
            style={{ minHeight: "calc(100vh - 70px)" }}
            onError={() => setIframeFailed(true)}
            data-testid="menu-pdf-iframe"
          />
        ) : (
          <iframe
            src={gdocsViewerUrl}
            title={`${brand.name} menu (via viewer)`}
            className="w-full h-full border-0"
            style={{ minHeight: "calc(100vh - 70px)" }}
            data-testid="menu-pdf-gdocs"
          />
        )}
      </div>
    </main>
  );
}
